import {
  AudienceAgeSelection,
  BaseFilterOption,
  DynamicPlatformFilters,
  Filter,
  FiltersGroup,
  MultiSelection,
  MultiSelectionAsync,
  MultiUserInput,
  RangeSelection,
  SingleSelection,
  SingleUserInput,
  WeightedFilterOption,
  WeightedMultiSelection,
} from '@/types/models/creator-crm/filter';
import {FilterIds, FiltersGroupId} from '@/types/models/creator-crm/filterId';

type AppliedFilters = Partial<Record<FilterIds, unknown>>;
type AppliedFiltersGroups = Partial<Record<FiltersGroupId, AppliedFilters>>;

function ensureValuesExist<T extends object>(object: T): T | undefined {
  if (Object.values(object).some((value) => value !== undefined)) {
    return object;
  }
  return undefined;
}

function mapToAppliedFilters(
  filterGroups: Record<FiltersGroupId, FiltersGroup<FilterIds>>
): AppliedFiltersGroups | undefined {
  const appliedFiltersGroups: AppliedFiltersGroups = {};

  function getSelected(filter: Filter): unknown | undefined {
    switch (filter.type) {
      case 'singleSelection': {
        const {selected} = filter as SingleSelection<BaseFilterOption>;
        if (filter.asyncInitializationKey) {
          return selected;
        }
        return selected?.value ? selected.id : undefined;
      }
      case 'weightedSingleSelection': {
        const {selected} = filter as SingleSelection<WeightedFilterOption>;
        if (filter.asyncInitializationKey) {
          return selected;
        }
        return selected?.value ? [selected.id, selected.weight] : undefined;
      }
      case 'multiSelection': {
        const {selected} = filter as MultiSelection<BaseFilterOption>;
        if (filter.asyncInitializationKey && selected.length) {
          return selected;
        }
        if (selected.length) {
          return selected.map((option) => option.id);
        }
        return undefined;
      }
      case 'weightedMultiSelection': {
        const {selected} = filter as MultiSelection<WeightedFilterOption>;
        if (filter.asyncInitializationKey && selected.length) {
          return selected;
        }
        if (selected.length) {
          return selected.map((option) => [option.value, option.weight]);
        }
        return undefined;
      }
      case 'multiSelectionAsync': {
        if ((filter as MultiSelection<WeightedFilterOption>).selected.length) {
          return (filter as MultiSelection<WeightedFilterOption>).selected;
        }
        return undefined;
      }
      case 'audienceAge':
        return (
          (filter as AudienceAgeSelection).ldaSelected ||
          ((filter as AudienceAgeSelection).selected.length
            ? (filter as AudienceAgeSelection).selected.map((option) => [
                option.id,
                option.weight,
              ])
            : undefined)
        );
      case 'rangeSelection':
        return (filter as RangeSelection).selected;
      case 'multiUserInput':
        return (filter as MultiUserInput).inputs.length
          ? (filter as MultiUserInput).inputs
          : undefined;
      case 'singleUserInput':
        return (filter as SingleUserInput).input
          ? (filter as SingleUserInput).input
          : undefined;
      case 'dynamicPlatformFilters': {
        const dpf = filter as DynamicPlatformFilters;
        const selectedPlatformFilter = dpf.platformsFilters[dpf.platformId];
        const appliedPlatformFilters: Partial<Record<string, unknown>> = {};
        Object.entries(selectedPlatformFilter || {}).forEach(
          ([platformFilterId, platformFilter]) => {
            const selected = getSelected(platformFilter);
            if (selected) {
              appliedPlatformFilters[platformFilterId] = selected;
            }
          }
        );
        const ensuredAppliedPlatformFilters = ensureValuesExist(
          appliedPlatformFilters
        );
        return ensuredAppliedPlatformFilters
          ? [dpf.platformId, ensuredAppliedPlatformFilters]
          : undefined;
      }
      default:
        return undefined;
    }
  }

  Object.entries(filterGroups).forEach(([groupId, group]) => {
    const appliedFilters: AppliedFilters = {};
    Object.entries(group.filters).forEach(([filterId, filter]) => {
      const selected = getSelected(filter);
      if (selected) {
        appliedFilters[filterId as FilterIds] = selected;
      }
    });
    const ensuredAppliedFilters = ensureValuesExist(appliedFilters);
    if (ensuredAppliedFilters) {
      appliedFiltersGroups[groupId as FiltersGroupId] = ensuredAppliedFilters;
    }
  });
  return ensureValuesExist(appliedFiltersGroups);
}

export default mapToAppliedFilters;
